/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import ox from '@/ox'
import _ from '@/underscore'
import { defineStore } from 'pinia'
import { reactive, toRef } from 'vue'
import { settings as coreSettings } from '@/io.ox/core/settings'
import { addReadyListener } from '@/io.ox/core/events'
import { getItem, getKeys, removeItem, setItem } from '@/io.ox/core/bimi/util'
import { getJWT } from '@/io.ox/core/auth.js'

const blocklist = new Set([
  // skip the following domains (typical mail providers domains)
  'gmail.com', 'icloud.com', 'outlook.com', 'yahoo.com', 'live.com', 'hotmail.com', 'qq.com', '163.com', 'yandex.com', 'aol.com', // global
  'gmx.com', 'gmx.net', 'gmx.de', 'web.de', 't-online.de', 'mailbox.org', // DE
  'orange.fr', 'free.fr', 'sfr.fr', // FR
  'libero.it', 'virgilio.it', 'tim.it', // IT
  'telefonica.net' // ES
])

addReadyListener('settings', () => {
  const list = coreSettings.get('bimi/blocklist')
  if (Array.isArray(list)) list.forEach(domain => blocklist.add(normalizeDomain(domain)))
})

export const useBimiStore = defineStore('bimi', () => {
  const defaults = { hasRecord: false, certificateType: 'none', svgContent: '' }
  const records = reactive({
    '': { ...defaults }
  })
  const psl = reactive({})
  const inflight = {}

  function getRecord (domain = '') {
    if (!records[domain]) {
      const item = getItem(`bimi/${domain}`)
      if (item) {
        // update timestamp in localStorage (relevant for GC)
        const record = records[domain] = { ...defaults, hasRecord: true, ...JSON.parse(item) }
        record.t = new Date().valueOf()
        setItem(`bimi/${domain}`, JSON.stringify(record))
      } else {
        records[domain] = { domain, ...defaults }
      }
    }
    return toRef(records, domain)
  }

  function addRecord (domain = '', record = {}) {
    if (!domain) return toRef(records, '')
    const value = { ...defaults, hasRecord: true, ...record }
    records[domain] = value
    setItem(`bimi/${domain}`, JSON.stringify({ ...value, t: new Date().valueOf() }))
    removeOldRecords()
    return toRef(records, domain)
  }

  async function fetchBIMIRecord (domain = '') {
    // normalize and check blocklist first
    domain = normalizeDomain(domain)
    if (!domain || blocklist.has(domain)) return null
    // in store already (reactive cache or localStorage)?
    const existing = getRecord(domain)
    if (existing.value?.hasRecord) return existing
    // already fetching?
    if (inflight[domain]) return inflight[domain]
    // otherwise fetch now (add promise to pending to avoid duplicate requests)
    return (inflight[domain] = _fetchRecord(domain).finally(() => delete inflight[domain]))
  }

  async function _fetchRecord (domain) {
    const jwt = await getJWT()
    if (!jwt) return null

    const response = await fetch(`${ox.root}/ui/v1/bimi/${encodeURIComponent(domain)}?includeLogo=true`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' }
    })
    if (!response.ok) return null

    const result = await response.json()
    psl[domain] = result.domain
    addRecord(domain, result)
    return getRecord(domain)
  }

  function getPublicSuffix (domain) {
    domain = normalizeDomain(domain)
    if (!psl[domain]) psl[domain] = domain
    return toRef(psl, domain)
  }

  return {
    fetchBIMIRecord,
    getRecord,
    addRecord,
    getPublicSuffix
  }
})

const store = useBimiStore()
if (ox.debug) window.bimiStore = store

export async function getBIMILogoAsDataUrl (domain) {
  const bimi = await store.fetchBIMIRecord(domain)
  if (!bimi?.value) return ''
  if (!bimi.value.svgContent) return ''
  const encoded = btoa(unescape(encodeURIComponent(bimi.value.svgContent)))
  return `data:image/svg+xml;base64,${encoded}`
}

export function getDomainFromEmailAddress (emailAddress = '') {
  return normalizeDomain(emailAddress).split('@').at(-1)
}

export function normalizeDomain (domain = '') {
  return String(domain || '').trim().toLowerCase()
}

export const removeOldRecords = _.debounce(function bimiGC () {
  const now = new Date().valueOf()
  const DAY = 24 * 3_600 * 1_000
  for (const key of getKeys()) {
    if (!/^bimi\//.test(key)) continue
    try {
      const item = JSON.parse(getItem(key))
      const ageInDays = (now - (item.t || 0)) / DAY >> 0
      if (item.certificateType !== 'none' && ageInDays < 90) continue
      if (item.certificateType === 'none' && ageInDays < 30) continue
      removeItem(key)
    } catch (err) {
      if (ox.debug) console.error(err)
    }
  }
}, 1000)
